import React, { useContext, useEffect, useState } from 'react';
import classes from './../BulkImportContent/BulkImportContent.module.scss';
import linkClasses from './BulkImportLinkSettings.module.scss';
import { UseFormReturn } from 'react-hook-form';
import { BulkActionsContext } from '../states/BulkActionsProvider';
import { LinkClickTracking, LinkDestination, PlanAnalyticsType } from '../../../models/graphql';
import { CheckboxWithAction } from '../../../components/actions/CheckboxWithAction';
import classNames from 'classnames';
import { FormElementAction } from '../../../models/basic-objects';
import { EditUtmModal } from '../../Settings/LinkTracking/EditUtmModal';
import { Select } from '../../../components/forms/Select';
import { Toggle } from '../../../components/forms/Toggle';
import { ToggleAction } from '../../../components/forms/Toggle/Toggle.controller';
import { Tooltip } from '../../../components/actions/Tooltip';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { EditUtmModalViewProps } from '../../Settings/LinkTracking/EditUtmModal/EditUtmModal.view';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export type BulkImportLinkSettingsViewProps = {
  //
  formInstance: UseFormReturn;
};

export const BulkImportLinkSettingsView = (props: BulkImportLinkSettingsViewProps): JSX.Element => {
  const { formInstance } = props;
  const { utm, state, dispatch } = useContext(BulkActionsContext);
  const [editUtmModal, setEditUtmModal] = useState<boolean>(false);
  const { analyticsType, isTrial } = useOrganizationPlan();
  const { linkSettings } = state;
  const { checkboxes, content } = linkSettings;
  const { keepExistingUtm, addUtmCampaignTracking, bitly, linkDestination, utmSettings } = content;

  const checkboxesState = Object.values(checkboxes).reduce((prev, current) => {
    if (prev) return prev;
    return current;
  });

  const {
    keepExistingUtm: keepExistingUtmCheckbox,
    addUtmCampaignTracking: addUtmCampaignTrackingCheckbox,
    bitly: bitlyCheckbox,
    linkDestination: linkDestinationCheckbox
  } = checkboxes;

  const setKeepExistingUtmHandler: ToggleAction = (payload, complete) => {
    complete();
    dispatch({ type: 'setKeepExisting', payload });
  };

  const setUtmCampaignTrackingHandler: ToggleAction = (payload, complete) => {
    complete();
    dispatch({ type: 'setUtmCampaignTracking', payload });
  };

  const setBitlyHandler: FormElementAction = (value, complete) => {
    complete();
    const bitlyValue: LinkClickTracking =
      value === LinkClickTracking.BITLY ? LinkClickTracking.BITLY : LinkClickTracking.NONE;
    dispatch({ type: 'setBitly', payload: bitlyValue });
  };

  const setLinkDestinationHandler: ToggleAction = (value, complete) => {
    complete();
    const linkDestinationValue = value ? LinkDestination.EXPANDED : LinkDestination.INITIAL;
    dispatch({ type: 'setLinkDestination', payload: linkDestinationValue });
  };

  const utmCallbackHandler: EditUtmModalViewProps['submitCallback'] = payload => {
    dispatch({ type: 'setUtmSettings', payload });
  };

  useEffect(() => {
    dispatch({ type: 'setChanges', payload: { section: 'linkSettings', value: checkboxesState } });
  }, [checkboxesState, dispatch]);

  useEffect(() => {
    dispatch({ type: 'setUtmSettings', payload: utm });
  }, [utm, dispatch]);

  return (
    <>
      {/* Link Destination */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'linkDestinationCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'linkSettings', key: 'linkDestination' } });
            }}
            wrapper={true}
            checked={linkDestinationCheckbox}
            label={'ORIGINAL OR REDIRECTED LINK'}
          />
        </div>
        <div className={classNames(classes.InnerBlock)}>
          <div className={'grid-x'}>
            <div className={'cell shrink'}>
              <Toggle
                disabled={!linkDestinationCheckbox}
                checked={linkDestination === LinkDestination.EXPANDED}
                formInstance={formInstance}
                name={'LinkDestinationToggle'}
                rules={{}}
                action={setLinkDestinationHandler}
                label={'Use redirected link'}
              />
            </div>
            <div className={'cell shrink'}>
              <Tooltip
                type={'inlined'}
                position={'right'}
                pointer={true}
                autoZIndex={false}
                style={{ zIndex: scssVars['z-index-overmodal'] }}
                disabled={!linkDestinationCheckbox}
                target={'#linkDestinationSection'}>
                <>
                  Choose whether to keep the link as you entered it, or share the destination URL that the link
                  redirects to
                </>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      {/* keep Existing UTM */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'keepExistingUtmCheckbox'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'linkSettings', key: 'keepExistingUtm' } });
            }}
            wrapper={true}
            checked={keepExistingUtmCheckbox}
            label={'EXISTING UTM'}
          />
        </div>
        <div className={classNames(classes.InnerBlock)}>
          <div className={'grid-x'}>
            <div className={'cell shrink'}>
              <Toggle
                formInstance={formInstance}
                name={'keepExistingUtmToogle'}
                rules={{}}
                action={setKeepExistingUtmHandler}
                checked={keepExistingUtm}
                label={'Keep original UTM parameters'}
                disabled={!keepExistingUtmCheckbox}
              />
            </div>
            <div className={'cell shrink'}>
              <Tooltip
                type={'inlined'}
                position={'right'}
                pointer={true}
                autoZIndex={false}
                disabled={!keepExistingUtmCheckbox}
                style={{ zIndex: scssVars['z-index-overmodal'] }}
                target={'#keepExistingUtmSection'}>
                <>
                  If you plan to add your own UTM parameters below, or prefer a cleaner URL for sharing, you can opt to
                  remove any existing UTM parameters from the link.
                </>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      {/* Google Analytics */}
      <div className={'grid-x'}>
        <div className={classNames('cell small-12', linkClasses.AdvancedFeatureContainer)}>
          <CheckboxWithAction
            name={'addUtmCampaignTracking'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'linkSettings', key: 'addUtmCampaignTracking' } });
            }}
            wrapper={true}
            disabled={analyticsType === PlanAnalyticsType.BASIC_ANALYTICS}
            checked={addUtmCampaignTrackingCheckbox}
            label={'GOOGLE ANALYTICS'}
          />
          {(analyticsType === PlanAnalyticsType.BASIC_ANALYTICS || isTrial) && (
            <AdvancedFeature feature={AdvancedFeatures.google}></AdvancedFeature>
          )}
        </div>
        <div className={classNames(classes.InnerBlock)}>
          <div className={'grid-x'}>
            <div className={'cell shrink'}>
              <Toggle
                wrapper
                formInstance={formInstance}
                name={'addUtmCampaignTrackingToggle'}
                rules={{}}
                action={setUtmCampaignTrackingHandler}
                checked={addUtmCampaignTracking}
                label={'Add UTM Parameters'}
                disabled={!addUtmCampaignTrackingCheckbox}
              />
            </div>
            <div className={'cell shrink'}>
              <Tooltip
                type={'inlined'}
                position={'right'}
                pointer={true}
                autoZIndex={false}
                disabled={!addUtmCampaignTrackingCheckbox}
                style={{ zIndex: scssVars['z-index-overmodal'] }}
                target={'#addUtmCampaignTrackingSection'}>
                <>Track social traffic in Google Analytics by adding UTM parameters to your links</>
              </Tooltip>
            </div>
          </div>
          <div>
            <a
              className={classNames(
                classes.EditUtmHandler,
                addUtmCampaignTrackingCheckbox && addUtmCampaignTracking ? '' : classes.EditUtmHandlerDisabled
              )}
              onClick={() => setEditUtmModal(true)}>
              Edit UTM Settings
            </a>
          </div>
          {keepExistingUtmCheckbox && keepExistingUtm && (
            <div className={'margin-top-small-16'}>
              <BoxNotification type={'info'}>Note that existing UTM parameters will not be overwritten</BoxNotification>
            </div>
          )}
          {!keepExistingUtmCheckbox && (
            <div className={'margin-top-small-16'}>
              <BoxNotification type={'info'}>
                Note that these UTM options may be overriden for each individual post
              </BoxNotification>
            </div>
          )}
        </div>
      </div>
      {/* Bitly Settings */}
      <div className={'grid-x'}>
        <div className={'cell small-12'}>
          <CheckboxWithAction
            name={'bitly'}
            size={'small'}
            onClickHandler={() => {
              dispatch({ type: 'toggleCheckbox', payload: { section: 'linkSettings', key: 'bitly' } });
            }}
            wrapper={true}
            checked={bitlyCheckbox}
            label={'CLICK TRACKING'}
          />
        </div>
        <div className={classNames(classes.InnerBlock)}>
          <div className={'cell shrink'}>
            <Select
              disabled={!bitlyCheckbox}
              items={[
                {
                  label: 'Do not use Bitly',
                  value: LinkClickTracking.NONE
                },
                { label: 'Use Defaults', value: LinkClickTracking.BITLY }
              ]}
              action={setBitlyHandler}
              name={'bitly'}
              rules={{}}
              size={'large'}
              useFormInstance={formInstance}
              hideDefaultSelectOption={true}
              wrapper={true}
              selectedValue={bitly}
            />
            <a rel={'noreferrer'} target={'_blank'} href={'/settings/tracking'}>
              View Default Bitly Settings
            </a>
          </div>
        </div>
      </div>
      {editUtmModal ? (
        <EditUtmModal
          onClose={() => setEditUtmModal(false)}
          campaign={utmSettings}
          submitCallback={utmCallbackHandler}
        />
      ) : null}
    </>
  );
};
